/**
 * Client helpers for /api/windowdoor/servicerequests.
 *
 * Every page and widget goes through this module — nothing else should build
 * a service-request URL by hand.
 */
const BASE = "/api/windowdoor/servicerequests";

const asJson = async (response) => {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
    } catch (err) {
      // Non-JSON error body; the status is all we have.
    }
    throw new Error(message);
  }
  return response.json();
};

const post = (url, payload) =>
  fetch(url, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).then(asJson);

/**
 * List requests.
 * scope: "all" (shop), "mine" (submitter), "assigned" (technician).
 * status: "all" | "open" | an exact status string.
 */
export const fetchServiceRequests = async ({
  scope = "all",
  status = "all",
  user,
} = {}) => {
  const params = new URLSearchParams({ scope, status });
  if (user?.userID) params.set("userID", user.userID);
  if (user?.email) params.set("email", user.email);
  return fetch(`${BASE}?${params.toString()}`).then(asJson);
};

export const fetchServiceRequestStats = async () =>
  fetch(`${BASE}/stats`).then(asJson);

export const fetchServiceRequest = async (id) =>
  fetch(`${BASE}/${id}`).then(asJson);

export const fetchServiceTechnicians = async () =>
  fetch(`${BASE}/technicians`).then(asJson);

/**
 * Submit a new request. Sent as multipart so the photos ride along with the
 * fields in one round trip; `user` is JSON-encoded inside the form data.
 */
export const submitServiceRequest = async (values, files, user) => {
  const body = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    body.append(key, value ?? "");
  });
  body.append("user", JSON.stringify(user || {}));
  (files || []).forEach((file) => body.append("files", file));

  return fetch(BASE, { method: "POST", body }).then(asJson);
};

export const saveServiceRequestChecklist = async (id, checklist, updatedBy) =>
  post(`${BASE}/${id}/checklist`, { checklist, updated_by: updatedBy });

export const assignServiceRequest = async (id, assignedTo, updatedBy) =>
  post(`${BASE}/${id}/assign`, {
    assigned_to: assignedTo,
    updated_by: updatedBy,
  });

/** action: "hold" | "unhold" | "take_off" | "reopen" */
export const updateServiceRequestStatus = async (id, action, updatedBy, note) =>
  post(`${BASE}/${id}/status`, { action, updated_by: updatedBy, note });

/** Shop-side edit of the request's detail fields; server diffs and logs. */
export const updateServiceRequestDetails = async (id, fields, updatedBy) =>
  post(`${BASE}/${id}/details`, { fields, updated_by: updatedBy });

export const fetchServiceRequestMessages = async (id) =>
  fetch(`${BASE}/${id}/messages`).then(asJson);

export const postServiceRequestMessage = async (id, message, from) =>
  post(`${BASE}/${id}/messages`, { message, from });

/** Add attachments to an existing request (multipart, like submit). */
export const uploadServiceRequestFiles = async (id, files, user) => {
  const body = new FormData();
  (files || []).forEach((file) => body.append("files", file));
  body.append("user", JSON.stringify(user || {}));
  return fetch(`${BASE}/${id}/files`, { method: "POST", body }).then(asJson);
};

/**
 * Activity log, newest first: one request's (every change and every email
 * about it, sent or failed), or the whole queue's when id is omitted.
 */
export const fetchServiceRequestActivity = async (id) =>
  fetch(id ? `${BASE}/${id}/activity` : `${BASE}/activity`).then(asJson);

export const serviceRequestFileUrl = (id, storedName) =>
  `${BASE}/${id}/files/${encodeURIComponent(storedName)}`;

export const fetchServiceRequestNotificationSettings = async () =>
  fetch(`${BASE}/notifications`).then(asJson);

export const saveServiceRequestNotificationSettings = async (settings) =>
  post(`${BASE}/notifications`, settings);
